import { Button } from '@insify/design-system/components';
import { IconicClose, IconicQuestionMarkCircle, IconicWarningTriangle } from '@insify/design-system/iconic-icons';
import { useBreakpoint } from '@insify/react-hooks';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatches } from 'react-router-dom';

import { useUiStore } from '../../stores/uiStore';
import { useCountrySpecificContent } from '../../utils/localize';
import { Breadcrumb } from '../Breadcrumb/Breadcrumb';
import { CenterAlignedContainer } from '../CenterAlignedContainer';

import styles from './style.module.css';

import type { Bundle } from '../../types/Policy';

interface ComplexCrumb {
  header: string;
  crumbTitle: string;
}

interface CrumbHandle {
  crumb: (data?: Bundle) => string | ComplexCrumb;
}

const SupportButton = () => {
  const { landing_page_contact } = useCountrySpecificContent();

  return (
    <Button
      kind='neutral'
      trackingId='support'
      href={landing_page_contact}
      target='_blank'
      leftIcon={<IconicQuestionMarkCircle />}
      className={styles.headerButton}
    />
  );
};

const TransitioningHeader: React.FC = () => {
  const { t } = useTranslation('navigation');
  const { notification, resetNotification } = useUiStore();
  const matches = useMatches();
  const crumbs = matches
    .filter((match) => Boolean((match.handle as CrumbHandle)?.crumb))
    .map((match) => {
      const matchResult = (match.handle as CrumbHandle).crumb(match.data as Bundle);

      if (!matchResult) {
        return { ...match, titleKey: '', header: undefined };
      }
      if (typeof matchResult === 'string') {
        return { ...match, titleKey: matchResult, header: matchResult };
      }
      return { ...match, titleKey: matchResult.crumbTitle, header: matchResult.header };
    });

  const [scrollY, setScrollY] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const largeHeaderRef = useRef<HTMLDivElement>(null);

  const isMobile = !useBreakpoint('max-width: 960px');

  const titleKey = crumbs[crumbs.length - 1]?.header || crumbs[crumbs.length - 1]?.titleKey || 'home';

  const largeHeaderOpacity = headerHeight <= 0 ? 1 : Math.max(0, 1 - scrollY / headerHeight);
  const smallHeaderOpacity = headerHeight <= 0 ? 0 : Math.min(1, scrollY / headerHeight);

  useEffect(() => {
    const updateHeaderHeight = () => {
      if (largeHeaderRef.current) {
        const height = largeHeaderRef.current.offsetHeight / 2.5;
        setHeaderHeight(height > 0 ? height - 20 : 0);
      }
    };

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, []);

  return (
    <>
      <div className={styles.header}>
        {!!notification && (
          <div className={styles.notification}>
            <CenterAlignedContainer className={styles.innerNotification}>
              <IconicWarningTriangle width={25} height={25} style={{ flexShrink: 0 }} color='var(--brown-80)' />
              {notification} <IconicClose className={styles.closeNotification} onClick={resetNotification} />
            </CenterAlignedContainer>
          </div>
        )}
        <Breadcrumb crumbs={crumbs} />

        <div
          className={styles.headerTitle}
          style={{
            opacity: smallHeaderOpacity,
            pointerEvents: smallHeaderOpacity > 0.3 ? 'auto' : 'none',
          }}
        >
          <h1 className={styles.headerTitleSm}>{t(titleKey)}</h1>
        </div>
        <SupportButton />
      </div>

      <div
        ref={largeHeaderRef}
        className={styles.headerLarge}
        style={{
          opacity: largeHeaderOpacity,
          marginBottom: -headerHeight * (1 - largeHeaderOpacity),
          pointerEvents: largeHeaderOpacity > 0.5 ? 'auto' : 'none',
        }}
      >
        {!isMobile && (
          <div className={styles.headerTopRow}>
            <Breadcrumb crumbs={crumbs} />
            {/* <div className='header-placeholder'></div> */}
            <SupportButton />
          </div>
        )}
        <CenterAlignedContainer>
          <h1 className={styles.headerTitleLg}>{t(titleKey)}</h1>
        </CenterAlignedContainer>
      </div>

      {/* <div style={{ height: headerHeight * largeHeaderOpacity }} /> */}
    </>
  );
};

export { TransitioningHeader };
